/**
 * Container Security API module.
 *
 * Real per-domain split — this file used to be a stub re-export from `../api`.
 * The `containerSecurityApi` export below was extracted verbatim from the
 * monolithic `frontend/src/services/api.ts` so consumers can import it directly
 * from `services/api/container` without round-tripping through the monolithic
 * file. Behavior is unchanged.
 *
 * Domain object:
 *   - containerSecurityApi  (image scanner, K8s security, registry security,
 *                            runtime security, admission controller, SBOM,
 *                            malware scan, lateral movement, PSS, workload
 *                            risk, runtime SSE stream, Wiz parity extensions
 *                            for workload-identity / network-segmentation /
 *                            service-mesh / container compliance / aggregator
 *                            dashboard / runtime compliance & threat intel /
 *                            registry enhanced)
 *
 * Note: imageDriftApi and snapshotScanApi are CWPP-domain; they are NOT
 * pulled in here.
 */
import { _unwrap } from './_unwrap';
import { apiClient as fetchClient } from './client';
import { API_CONFIG } from './config';
import {
  CloudProvider,
  SeverityLevel,
  ContainerImage,
  ContainerImageDetail,
  ImageLayer,
  SBOMEntry,
  KubernetesCluster,
  KubernetesClusterDetail,
  K8sNamespace,
  K8sSecurityIssue,
  K8sRBACFinding,
  K8sNetworkPolicy,
  K8sWorkload,
} from '../../types';

export const containerSecurityApi = {
  // Image Scanner
  getImages: async (params?: {
    registry?: string;
    has_vulnerabilities?: boolean;
    page?: number;
    page_size?: number;
  }): Promise<{ images: ContainerImage[]; total: number }> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/containers/images', params));
  },

  getImage: async (id: string): Promise<ContainerImage> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/containers/images/${id}`));
  },

  getImageDetail: async (id: string): Promise<ContainerImageDetail> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/containers/images/${id}/detail`));
  },

  getImageLayers: async (id: string): Promise<{ layers: ImageLayer[] }> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/containers/images/${id}/layers`));
  },

  getImageSBOM: async (id: string): Promise<{ sbom: SBOMEntry[]; total: number }> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/containers/images/${id}/sbom`));
  },

  scanImage: async (imageRef: string): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cwpp/containers/images/scan', {
      image_ref: imageRef,
    }));
  },

  // Kubernetes Security
  getClusters: async (params?: {
    provider?: CloudProvider;
    page?: number;
    page_size?: number;
  }): Promise<{ clusters: KubernetesCluster[]; total: number }> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/kubernetes/clusters', params));
  },

  getCluster: async (id: string): Promise<KubernetesCluster> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/kubernetes/clusters/${id}`));
  },

  getClusterDetail: async (id: string): Promise<KubernetesClusterDetail> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/kubernetes/clusters/${id}/detail`));
  },

  getClusterNamespaces: async (id: string): Promise<{ namespaces: K8sNamespace[] }> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/kubernetes/clusters/${id}/namespaces`));
  },

  getClusterSecurityIssues: async (
    id: string,
    params?: { severity?: SeverityLevel; category?: string }
  ): Promise<{ security_issues: K8sSecurityIssue[]; total: number }> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/kubernetes/clusters/${id}/security-issues`, params));
  },

  getClusterRBACFindings: async (id: string): Promise<{ rbac_findings: K8sRBACFinding[] }> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/kubernetes/clusters/${id}/rbac`));
  },

  getClusterNetworkPolicies: async (id: string): Promise<{ network_policies: K8sNetworkPolicy[] }> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/kubernetes/clusters/${id}/network-policies`));
  },

  getClusterWorkloads: async (
    id: string,
    params?: { namespace?: string }
  ): Promise<{ workloads: K8sWorkload[]; total: number }> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/kubernetes/clusters/${id}/workloads`, params));
  },

  getClusterFindings: async (clusterId: string): Promise<any[]> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/kubernetes/clusters/${clusterId}/findings`));
  },

  // Registry Security
  getRegistries: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/containers/registries'));
  },

  getRegistry: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/cwpp/containers/registries/${id}`));
  },

  // R103 — registry connection management
  connectRegistry: async (data: { name: string; type: string; url: string; auth?: any; credentials?: any }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/cwpp/containers/registries', data));
  },
  syncRegistry: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cwpp/containers/registries/${id}/sync`, {}));
  },
  scanAllRegistry: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/cwpp/containers/registries/${id}/scan-all`, {}));
  },
  // R140 / dedup: getRegistryCompliance + getRegistryImages were defined here
  // and again at lines 208-216 with the newer /container-security/ paths.
  // Kept the newer expanded versions; removed the cwpp/containers duplicates.

  // Runtime Security
  getRuntimeEvents: async (params?: { severity?: string }): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/containers/runtime/events', params));
  },

  getRuntimePolicies: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/cwpp/containers/runtime/policies'));
  },

  getDashboard: async (): Promise<any> => {
    // /cwpp/containers/images/statistics 404s (route not served). Use the
    // container aggregator's real dashboard summary, which returns a valid stats
    // shape (and will surface live image counts once the aggregator reads the DB).
    return _unwrap(await fetchClient.get('/api/v1/container/dashboard'));
  },

  // Image Scanner - Expanded
  getImageSBOMSpdx: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/images/${id}/sbom/spdx`));
  },
  getImageSBOMCycloneDX: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/images/${id}/sbom/cyclonedx`));
  },
  getImageBaseImage: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/images/${id}/base-image`));
  },
  getImageSupplyChain: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/images/${id}/supply-chain`));
  },
  getImageSignatures: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/images/${id}/signatures`));
  },
  // R210 — Wiz parity: cosign trust-policy CRUD. BE at /api/v1/images/trust-policy*
  // (image-scanner/main.py:2760+). FE wrappers + UI panel were missing.
  listTrustPolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/images/trust-policy'));
  },
  getTrustPolicy: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/images/trust-policy/${id}`));
  },
  createTrustPolicy: async (data: {
    name: string;
    require_signature?: boolean;
    allowed_signers?: string[];
    allowed_registries?: string[];
    signature_types?: string[];
    enforce?: boolean;
    description?: string;
  }): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/images/trust-policy', data));
  },
  deleteTrustPolicy: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.delete(`/api/v1/images/trust-policy/${id}`));
  },
  getImageDependencies: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/images/${id}/dependencies`));
  },
  getImageLicenseCompliance: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/images/${id}/license-compliance`));
  },

  // K8s Security - Expanded
  validateAdmission: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/container-security/k8s/admission/validate', data));
  },
  mutateAdmission: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/container-security/k8s/admission/mutate', data));
  },
  getClusterCISBenchmark: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/k8s/clusters/${id}/cis-benchmark`));
  },
  getClusterSecretsAudit: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/k8s/clusters/${id}/secrets-audit`));
  },
  getClusterIngress: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/k8s/clusters/${id}/ingress`));
  },
  getClusterRemediation: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/k8s/clusters/${id}/remediation`));
  },

  // Registry Security - Expanded
  scanAllRegistries: async (): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/container-security/registries/scan-all'));
  },
  getRegistryImages: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/registries/${id}/images`));
  },
  getRegistryCompliance: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/registries/${id}/compliance`));
  },
  getRegistrySigningPolicies: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/registries/${id}/signing`));
  },
  getRegistryRetentionPolicies: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/registries/${id}/retention`));
  },
  getRegistryAccessAudit: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/registries/${id}/access-audit`));
  },
  getRegistryVulnReport: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/registries/${id}/vulnerability-report`));
  },

  // Runtime Security - Expanded
  getRuntimeEventDetail: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/runtime/events/${id}`));
  },
  investigateEvent: async (id: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/container-security/runtime/events/${id}/investigate`));
  },
  getRuntimeDriftDetection: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/runtime/drift'));
  },
  getRuntimeForensics: async (containerId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/runtime/forensics/${containerId}`));
  },
  getRuntimeNetworkMap: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/runtime/network-map'));
  },
  getRuntimeAnomalies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/runtime/anomalies'));
  },
  getRuntimeAttackPatterns: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/runtime/attack-patterns'));
  },
  quarantineContainer: async (containerId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/container-security/runtime/quarantine/${containerId}`));
  },
  simulateRuntimePolicy: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/container-security/runtime/policy-simulation', data));
  },

  // Runtime risk posture
  getRuntimeRiskPosture: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/runtime/risk-posture'));
  },

  // Admission controller events
  getAdmissionEvents: async (params?: { page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/k8s/admission/events', params));
  },

  // Admission controller policies
  getAdmissionPolicies: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/k8s/admission/policies'));
  },

  // Global CIS benchmark (across all clusters)
  getCISBenchmarkGlobal: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/k8s/cis-benchmark'));
  },

  // SBOM statistics
  getSBOMStatistics: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/images/sbom/statistics'));
  },

  // Malware scan
  scanImageMalware: async (imageId: string): Promise<any> => {
    return _unwrap(await fetchClient.post(`/api/v1/container-security/images/images/${imageId}/malware-scan`));
  },
  getImageMalware: async (imageId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/images/images/${imageId}/malware`));
  },
  getMalwareSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/images/malware/summary'));
  },

  // Lateral movement paths
  getLateralMovementPaths: async (params?: { min_risk_score?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/runtime/lateral-movement/paths', params));
  },
  getLateralMovementSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/runtime/lateral-movement/summary'));
  },

  // Pod Security Standards
  getPSSProfiles: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/k8s/pss/profiles'));
  },
  getPSSAssessment: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/k8s/pss/assessment/${clusterId}`));
  },

  // Workload risk prioritization
  getRiskPrioritizedWorkloads: async (params?: { page?: number; page_size?: number }): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/k8s/workloads/risk-prioritized', params));
  },

  // Admission Controller - Registry Management
  listAdmissionRegistries: async (): Promise<any[]> => {
    return _unwrap(await fetchClient.get('/api/v1/container/admission/registries'));
  },

  registerAdmissionRegistry: async (data: any): Promise<any> => {
    return _unwrap(await fetchClient.post('/api/v1/container/admission/registries', data));
  },

  // Runtime Events SSE Stream
  streamRuntimeEvents: (onMessage: (event: any) => void, onError?: (error: any) => void): EventSource => {
    const url = `${API_CONFIG.BASE_URL}/api/v1/container/runtime/events/stream`;
    const eventSource = new EventSource(url);
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch {
        onMessage(event.data);
      }
    };
    if (onError) {
      eventSource.onerror = onError;
    }
    return eventSource;
  },

  // ---- Wiz Parity: Workload Identity Mapping ----
  getWorkloadIdentityMappings: async (clusterId: string, params?: { over_privileged_only?: boolean; namespace?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/k8s/clusters/${clusterId}/workload-identity`, params));
  },
  getWorkloadIdentitySummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/k8s/workload-identity/summary'));
  },

  // ---- Wiz Parity: Network Segmentation Analysis ----
  getNetworkSegmentation: async (clusterId: string, params?: { traffic_type?: string; risk_level?: string }): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/k8s/clusters/${clusterId}/network-segmentation`, params));
  },
  getNetworkSegmentationSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/k8s/network-segmentation/summary'));
  },

  // ---- Wiz Parity: Service Mesh Security ----
  getServiceMeshStatus: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/k8s/clusters/${clusterId}/service-mesh`));
  },
  getServiceMeshSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/k8s/service-mesh/summary'));
  },

  // ---- Wiz Parity: Container Compliance Frameworks ----
  getContainerCompliance: async (clusterId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/k8s/clusters/${clusterId}/container-compliance`));
  },
  getContainerComplianceSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/k8s/container-compliance/summary'));
  },

  // ---- Wiz Parity: Aggregator Dashboard ----
  // These three used to call /api/v1/container-security/images/<name>. That
  // prefix rewrites to /api/v1/container/images/<name>, which lands in the
  // aggregator's `/api/v1/container/images/{path:path}` proxy and is forwarded
  // to the image-scanner as /api/v1/images/<name> — where no such route exists,
  // so `{image_id}` correctly answered 404 "Image not found". The aggregator's
  // dashboard/executive-summary/vulnerability-trends live one level up, under
  // /api/v1/container/, NOT under /images/. `getContainerDashboard` was deleted
  // rather than repointed: `getDashboard` above is the same call on the same
  // path and is the one OverviewDashboard.tsx actually uses.
  getExecutiveSummary: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container/executive-summary'));
  },
  getVulnerabilityTrends: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container/vulnerability-trends'));
  },

  // ---- Wiz Parity: Runtime Compliance & Threat Intelligence ----
  getRuntimeCompliance: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/runtime/runtime/compliance'));
  },
  getRuntimeThreatIntelligence: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/runtime/runtime/threat-intelligence'));
  },
  getRuntimeContainerInventory: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/runtime/runtime/container-inventory'));
  },

  // ---- Wiz Parity: Registry Enhanced ----
  getRegistrySecurityAssessment: async (registryId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/registries/${registryId}/security-assessment`));
  },
  getCrossRegistryAnalysis: async (): Promise<any> => {
    return _unwrap(await fetchClient.get('/api/v1/container-security/registries/registries/cross-registry-analysis'));
  },
  getRegistryImageAging: async (registryId: string): Promise<any> => {
    return _unwrap(await fetchClient.get(`/api/v1/container-security/registries/${registryId}/image-aging`));
  },
};
